import { useEffect, useState } from "react";
import { CalendarDays } from "lucide-react";
import { ptBR } from "date-fns/locale";
import { Input } from "./ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import { Calendar } from "./ui/calendar";

const toBR = (iso) => (/^\d{4}-\d{2}-\d{2}/.test(iso || "") ? `${iso.slice(8, 10)}/${iso.slice(5, 7)}/${iso.slice(0, 4)}` : "");
const mask = (s) => { const d = s.replace(/\D/g, "").slice(0, 8); return [d.slice(0, 2), d.slice(2, 4), d.slice(4)].filter(Boolean).join("/"); };
const parse = (br) => {
  const m = br.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
  if (!m) return null;
  const [, dd, mm, yy] = m;
  const dt = new Date(+yy, +mm - 1, +dd);
  return dt.getFullYear() === +yy && dt.getMonth() === +mm - 1 && dt.getDate() === +dd && +yy >= 1900 && +yy <= 2100 ? `${yy}-${mm}-${dd}` : null;
};
const toDate = (iso) => (iso ? new Date(+iso.slice(0, 4), +iso.slice(5, 7) - 1, +iso.slice(8, 10)) : undefined);
const toIso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
const DROP = {
  caption_dropdowns: "flex items-center gap-1", dropdown_month: "relative inline-flex items-center", dropdown_year: "relative inline-flex items-center",
  dropdown: "absolute inset-0 z-10 w-full cursor-pointer opacity-0", caption_label: "flex items-center gap-1 rounded-md px-1.5 py-0.5 text-sm font-medium capitalize hover:bg-slate-100", vhidden: "sr-only",
};

// pt-BR date field: type dd/mm/aaaa or pick on the calendar. Value stays ISO (AAAA-MM-DD).
export function DateInput({ value, onChange, testid, className = "", inputClassName = "", birth = false, max }) {
  const [text, setText] = useState(toBR(value));
  const [open, setOpen] = useState(false);
  useEffect(() => { if (parse(text) !== (value || null)) setText(toBR(value)); }, [value]); // eslint-disable-line react-hooks/exhaustive-deps
  const type = (raw) => {
    const t = mask(raw); setText(t);
    const iso = parse(t);
    if (iso && !(max && iso > max)) onChange(iso); else if (value) onChange("");
  };
  const bad = text.length === 10 && (!parse(text) || !!(max && parse(text) > max));
  const sel = toDate(value);
  const year = new Date().getFullYear();
  return (
    <div className={className}>
      <div className="relative">
        <Input data-testid={testid} inputMode="numeric" placeholder="dd/mm/aaaa" value={text} onChange={(e) => type(e.target.value)} aria-invalid={bad} className={`pr-10 ${bad ? "border-rose-400 focus-visible:ring-rose-300" : ""} ${inputClassName}`} />
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <button type="button" aria-label="Abrir calendário" data-testid={`${testid}-calendar`} className="absolute right-1 top-1/2 flex h-8 w-8 -translate-y-1/2 items-center justify-center rounded-md text-slate-500 transition-colors hover:bg-slate-100 hover:text-slate-800"><CalendarDays className="h-4 w-4" /></button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0" align="end" data-testid={`${testid}-popover`}>
            <Calendar mode="single" locale={ptBR} selected={sel} defaultMonth={sel || (birth ? new Date(1995, 0, 1) : new Date())} captionLayout="dropdown-buttons"
              fromYear={birth ? 1920 : 2000} toYear={birth ? year : year + 3} classNames={DROP} initialFocus disabled={max ? { after: toDate(max) } : undefined}
              onSelect={(d) => { if (d) { onChange(toIso(d)); setText(toBR(toIso(d))); } setOpen(false); }} />
          </PopoverContent>
        </Popover>
      </div>
      {bad && <div className="mt-1 text-[11px] text-rose-600" data-testid={`${testid}-invalid`}>Data inválida. Use dia/mês/ano.</div>}
    </div>);
}
